import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import { PADEL_MAX_SCORE, isSetComplete, type SetScoreInput } from './padelScoringUtils';
import {
  canBumpTennisScore,
  formatRallyScore,
  getTennisSetWinner,
  isTennisSetComplete,
  type TennisRallyState,
} from './padelTennisScoring';
import PadelPointPickerSheet from './PadelPointPickerSheet';

interface PadelScorePadProps {
  maxSets: number;
  activeSet: number;
  sets: SetScoreInput[];
  current: SetScoreInput;
  pickerTarget: number;
  americanoMode?: boolean;
  hint: string;
  onSelectSet: (setNumber: number) => void;
  onBump: (side: 'a' | 'b', delta: number) => void;
  onSetScore: (side: 'a' | 'b', value: number) => void;
  // Tennis Live Tracker additions
  rallyState?: TennisRallyState;
  onAddRallyPoint?: (side: 'a' | 'b') => void;
  onUndoRallyPoint?: () => void;
  canUndo?: boolean;
  manualEntry?: boolean;
  onToggleManualEntry?: (manual: boolean) => void;
  sideAName?: string;
  sideBName?: string;
  // Deuce addition
  deuceEnabled?: boolean;
  onToggleDeuce?: (enabled: boolean) => void;
}

export default function PadelScorePad({
  maxSets,
  activeSet,
  sets,
  current,
  pickerTarget,
  americanoMode = false,
  hint: _hint,
  onSelectSet,
  onBump,
  onSetScore,
  rallyState,
  onAddRallyPoint,
  onUndoRallyPoint,
  canUndo = false,
  manualEntry = false,
  onToggleManualEntry,
  sideAName = 'Tim A',
  sideBName = 'Tim B',
  deuceEnabled = false,
  onToggleDeuce,
}: PadelScorePadProps) {
  const [pickerSide, setPickerSide] = useState<'a' | 'b' | null>(null);
  const [activeSkorAkhirSide, setActiveSkorAkhirSide] = useState<'a' | 'b'>('a');

  const pickerValue = pickerSide === 'b' ? current.scoreSideB : current.scoreSideA;
  const rallyFormatted = rallyState ? formatRallyScore(rallyState) : null;
  const isTennisSets = !americanoMode;
  const isSetFinished = isTennisSets && isTennisSetComplete(current.scoreSideA, current.scoreSideB, deuceEnabled);
  const isSetDecided = isTennisSets && isSetComplete(current.scoreSideA, current.scoreSideB);
  const setWinnerSide = isSetDecided ? getTennisSetWinner(current.scoreSideA, current.scoreSideB) : '';
  const setWinnerName = setWinnerSide === 'a' ? sideAName : setWinnerSide === 'b' ? sideBName : '';

  return (
    <>
      {/* Set navigation pills */}
      {isTennisSets && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex flex-1 gap-2.5">
            {sets.map((set) => {
              const selected = activeSet === set.setNumber;
              const isUntouched = set.scoreSideA === 0 && set.scoreSideB === 0;
              const scoreText = isUntouched ? 'belum' : `${set.scoreSideA}-${set.scoreSideB}`;

              return (
                <button
                  key={set.setNumber}
                  type="button"
                  onClick={() => onSelectSet(set.setNumber)}
                  className={`flex-1 rounded-full py-2.5 px-3 text-center transition-all ${
                    selected
                      ? 'border-2 border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]'
                      : 'border border-gray-100 bg-white text-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className={`text-xs font-bold ${selected ? 'text-[#FC480D]' : 'text-gray-500'}`}>
                    Set {set.setNumber}
                  </div>
                  <div
                    className={`mt-0.5 text-[11px] font-semibold ${
                      selected ? 'text-[#FC480D]' : 'text-gray-400'
                    }`}
                  >
                    {scoreText}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Scoring Card */}
      <Card className="mt-3 overflow-hidden rounded-3xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
        {/* Segmented control: Live Score vs Skor Akhir */}
        {isTennisSets && onToggleManualEntry && (
          <div className="flex rounded-full bg-[#F3F4F6] p-1">
            <button
              type="button"
              onClick={() => onToggleManualEntry(false)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-bold transition-all ${
                !manualEntry
                  ? 'bg-[#FC480D] text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon icon="mdi:access-point" className="h-4 w-4" />
              <span>Live Score</span>
            </button>
            <button
              type="button"
              onClick={() => onToggleManualEntry(true)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 text-xs font-bold transition-all ${
                manualEntry
                  ? 'bg-[#FC480D] text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon icon="mdi:flag-outline" className="h-4 w-4" />
              <span>Skor Akhir</span>
            </button>
          </div>
        )}

        {/* --- VIEW 1: LIVE SCORE MODE --- */}
        {isTennisSets && !manualEntry && rallyFormatted ? (
          <div>
            {/* Live Point Boxes */}
            <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              {/* Sisi A Point Box */}
              <div
                className={`flex h-28 w-full items-center justify-center rounded-2xl text-5xl font-extrabold transition-all ${
                  rallyFormatted.scoreA === 'AD'
                    ? 'border-2 border-[#16A34A] bg-[#E8F8EE] text-[#16A34A]'
                    : 'border border-[#FED7AA] bg-[#FFF5F1] text-[#FC480D]'
                }`}
              >
                {rallyFormatted.scoreA}
              </div>

              <span className="text-xl font-bold text-gray-300">-</span>

              {/* Sisi B Point Box */}
              <div
                className={`flex h-28 w-full items-center justify-center rounded-2xl text-5xl font-extrabold transition-all ${
                  rallyFormatted.scoreB === 'AD'
                    ? 'border-2 border-[#16A34A] bg-[#E8F8EE] text-[#16A34A]'
                    : 'border border-[#FED7AA] bg-[#FFF5F1] text-[#FC480D]'
                }`}
              >
                {rallyFormatted.scoreB}
              </div>
            </div>

            {/* Point Action Buttons */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Button
                type="button"
                disabled={isSetFinished}
                onClick={() => onAddRallyPoint?.('a')}
                className="h-12 w-full gap-1.5 rounded-full bg-[#FC480D] text-sm font-bold text-white shadow-sm hover:bg-[#D83B08] active:scale-[0.98] disabled:opacity-40"
              >
                <Icon icon="mdi:plus" className="h-4 w-4" />
                Poin Sisi A
              </Button>
              <Button
                type="button"
                disabled={isSetFinished}
                onClick={() => onAddRallyPoint?.('b')}
                className="h-12 w-full gap-1.5 rounded-full bg-[#FC480D] text-sm font-bold text-white shadow-sm hover:bg-[#D83B08] active:scale-[0.98] disabled:opacity-40"
              >
                <Icon icon="mdi:plus" className="h-4 w-4" />
                Poin Sisi B
              </Button>
            </div>

            {/* Set finished notification */}
            {isSetFinished && (
              <div className="mt-3 flex flex-col items-center gap-2 rounded-2xl bg-emerald-50 p-3 text-center">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800">
                  <Icon icon="mdi:check-circle" className="h-4 w-4 text-emerald-600" />
                  Set {activeSet} Selesai ({current.scoreSideA} - {current.scoreSideB}) · Pemenang: {setWinnerName}
                </div>
                {activeSet < maxSets && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onSelectSet(activeSet + 1)}
                    className="h-8 rounded-xl bg-emerald-600 px-3.5 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    Lanjut ke Set {activeSet + 1}
                  </Button>
                )}
              </div>
            )}

            {/* Bottom Row: Undo Poin & Deuce Toggle Switch */}
            <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
              <button
                type="button"
                disabled={!canUndo}
                onClick={onUndoRallyPoint}
                className="flex h-10 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 text-xs font-bold text-gray-700 transition-all hover:bg-gray-50 active:scale-95 disabled:opacity-40"
              >
                <Icon icon="mdi:undo-variant" className="h-4 w-4 text-gray-500" />
                <span>Undo Poin</span>
              </button>

              <div className="flex items-center gap-2.5">
                <span className="text-xs font-bold text-[#0E1726]">Deuce</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={deuceEnabled}
                  onClick={() => onToggleDeuce?.(!deuceEnabled)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    deuceEnabled ? 'bg-[#FC480D]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                      deuceEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* --- VIEW 2: SKOR AKHIR / AMERICANO MODE --- */
          <div>
            {/* Score Display Boxes */}
            <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              {/* Sisi A Box */}
              <button
                type="button"
                aria-label="Poin game sisi A"
                onClick={() => {
                  setActiveSkorAkhirSide('a');
                  setPickerSide('a');
                }}
                className={`flex h-28 w-full items-center justify-center rounded-2xl text-5xl font-extrabold transition-all ${
                  activeSkorAkhirSide === 'a'
                    ? 'border-2 border-[#FC480D] bg-[#FFF5F1] text-[#FC480D]'
                    : 'border border-gray-100 bg-[#F1F5F9] text-[#0E1726]'
                }`}
              >
                {current.scoreSideA}
              </button>

              <span className="text-xl font-bold text-gray-300">-</span>

              {/* Sisi B Box */}
              <button
                type="button"
                aria-label="Poin game sisi B"
                onClick={() => {
                  setActiveSkorAkhirSide('b');
                  setPickerSide('b');
                }}
                className={`flex h-28 w-full items-center justify-center rounded-2xl text-5xl font-extrabold transition-all ${
                  activeSkorAkhirSide === 'b'
                    ? 'border-2 border-[#FC480D] bg-[#FFF5F1] text-[#FC480D]'
                    : 'border border-gray-100 bg-[#F1F5F9] text-[#0E1726]'
                }`}
              >
                {current.scoreSideB}
              </button>
            </div>

            {/* - and + Bump Buttons */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              {/* Sisi A */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  aria-label="Kurangi skor sisi A"
                  disabled={current.scoreSideA <= 0}
                  onClick={() => {
                    setActiveSkorAkhirSide('a');
                    onBump('a', -1);
                  }}
                  className="h-10 rounded-xl bg-[#F1F5F9] text-base font-bold text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                >
                  −
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  aria-label="Tambah skor sisi A"
                  disabled={
                    isTennisSets
                      ? !canBumpTennisScore(current.scoreSideA, current.scoreSideB, 'a', 1, deuceEnabled)
                      : current.scoreSideA >= PADEL_MAX_SCORE
                  }
                  onClick={() => {
                    setActiveSkorAkhirSide('a');
                    onBump('a', 1);
                  }}
                  className="h-10 rounded-xl bg-[#FFF5F1] text-base font-bold text-[#FC480D] hover:bg-[#FFE8DE] disabled:opacity-40"
                >
                  +
                </Button>
              </div>

              {/* Sisi B */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  aria-label="Kurangi skor sisi B"
                  disabled={current.scoreSideB <= 0}
                  onClick={() => {
                    setActiveSkorAkhirSide('b');
                    onBump('b', -1);
                  }}
                  className="h-10 rounded-xl bg-[#F1F5F9] text-base font-bold text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                >
                  −
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  aria-label="Tambah skor sisi B"
                  disabled={
                    isTennisSets
                      ? !canBumpTennisScore(current.scoreSideA, current.scoreSideB, 'b', 1, deuceEnabled)
                      : current.scoreSideB >= PADEL_MAX_SCORE
                  }
                  onClick={() => {
                    setActiveSkorAkhirSide('b');
                    onBump('b', 1);
                  }}
                  className="h-10 rounded-xl bg-[#FFF5F1] text-base font-bold text-[#FC480D] hover:bg-[#FFE8DE] disabled:opacity-40"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Set finished notification in Skor Akhir */}
            {isSetFinished && (
              <div className="mt-3 flex flex-col items-center gap-2 rounded-2xl bg-emerald-50 p-3 text-center">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800">
                  <Icon icon="mdi:check-circle" className="h-4 w-4 text-emerald-600" />
                  Set {activeSet} Selesai ({current.scoreSideA} - {current.scoreSideB}) · Pemenang: {setWinnerName}
                </div>
                {activeSet < maxSets && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onSelectSet(activeSet + 1)}
                    className="h-8 rounded-xl bg-emerald-600 px-3.5 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    Lanjut ke Set {activeSet + 1}
                  </Button>
                )}
              </div>
            )}

            {/* Bottom Row: Deuce Toggle Switch & Info for Skor Akhir */}
            {isTennisSets && (
              <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
                <div className="text-[11px] font-medium text-gray-400">
                  {deuceEnabled ? 'Deuce aktif (6-6 ➔ butuh selisih 2)' : 'Deuce nonaktif (skor 6-6 ➔ 7 menang)'}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-bold text-[#0E1726]">Deuce</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={deuceEnabled}
                    onClick={() => onToggleDeuce?.(!deuceEnabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      deuceEnabled ? 'bg-[#FC480D]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                        deuceEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <PadelPointPickerSheet
        open={pickerSide != null}
        side={pickerSide}
        currentValue={pickerValue}
        opponentValue={pickerSide === 'a' ? current.scoreSideB : current.scoreSideA}
        target={pickerTarget}
        americanoMode={americanoMode}
        onClose={() => setPickerSide(null)}
        onSelect={(value) => {
          if (pickerSide) onSetScore(pickerSide, value);
          setPickerSide(null);
        }}
      />
    </>
  );
}
